import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ExternalLink,
  ChevronRight,
  TrendingUp,
  Users,
  Briefcase,
  GraduationCap,
  PlayCircle,
} from "lucide-react";
import logo from "@/assets/logo_salone_vincente.png";
import { usePageViewTracking, getOrCreateSessionId } from "@/hooks/usePageViewTracking";
import { TRACKED_STEPS, ACADEMY_REQUEST_WEBHOOK_URL } from "@/config/tracking";



const COMMUNITY_URL = "https://salonevincenteacademy.app.clientclub.net/communities/groups/sv-academy/home";

const benefits = [
  {
    icon: TrendingUp,
    title: "Corsi di vendita",
    desc: "Tecniche pratiche per vendere di più in cabina",
  },
  {
    icon: Briefcase,
    title: "Gestione aziendale del centro",
    desc: "Numeri, margini e organizzazione per far crescere il tuo business.",
  },
  {
    icon: Users,
    title: "Gestione del personale",
    desc: "Come guidare, motivare e formare il tuo team estetico.",
  },
  {
    icon: GraduationCap,
    title: "Formazione per aumentare fatturato e profitto",
    desc: "Strategie testate per scalare i risultati del tuo centro.",
  },
  {
    icon: PlayCircle,
    title: "Registrazioni di tutti i webinar",
    desc: "Accesso illimitato all'archivio completo dei nostri webinar.",
  },
];

const Community = () => {
  usePageViewTracking(TRACKED_STEPS.community);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const goNext = () => {
    const qs = searchParams.toString();
    const groupId = searchParams.get("groupId");
    const Id = searchParams.get("Id") || searchParams.get("id");

    try {
      fetch(ACADEMY_REQUEST_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: "academy_request",
          groupId: groupId || null,
          Id: Id || null,
          sessionId: getOrCreateSessionId(),
          url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
          timestamp: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}

    navigate(`/4${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="min-h-screen gradient-subtle flex flex-col p-4">
      <div className="flex-1 flex items-center justify-center py-6">
        <div className="max-w-3xl w-full flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 animate-fade-in">
            <img
              src={logo}
              alt="Salone Vincente"
              className="w-14 h-14 md:w-16 md:h-16 object-contain animate-float flex-shrink-0"
            />
            <h1 className="flex-1 text-center text-xl md:text-3xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              ENTRA NELLA COMMUNITY DI SALONE VINCENTE
            </h1>
          </div>

          <div className="card p-6 md:p-8 shadow-elegant animate-slide-up space-y-6">
            <p className="text-base md:text-lg text-foreground text-center">
              All'interno della nostra <strong>community esclusiva</strong> trovi tutto ciò
              che serve per portare il tuo centro estetico a un livello superiore:
            </p>

            <div className="grid gap-3 md:gap-4">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.title}
                    className="flex items-start gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/10"
                  >
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Icon className="w-5 h-5 md:w-6 md:h-6 text-primary-foreground" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground text-sm md:text-base">
                        {b.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {b.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button
                size="lg"
                className="w-full whitespace-normal rounded-xl text-sm sm:text-base md:text-lg font-semibold px-6 py-5 sm:py-6 h-auto bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                ✨ Richiedi accesso alla community
                <ExternalLink className="w-4 h-4 md:w-5 md:h-5 ml-1" />
              </Button>
            </a>

            <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
              <Checkbox
                id="community-check"
                checked={checked}
                onCheckedChange={(v) => setChecked(v === true)}
                className="mt-1 h-5 w-5"
              />
              <label
                htmlFor="community-check"
                className="text-sm md:text-base font-medium text-foreground cursor-pointer select-none"
              >
                Ho fatto richiesta per entrare nella community
              </label>
            </div>

            <Button
              onClick={goNext}
              disabled={!checked}
              size="lg"
              className={`w-full whitespace-normal rounded-xl text-sm sm:text-base md:text-lg font-semibold px-6 py-5 sm:py-6 h-auto transition-all duration-300 ${
                checked
                  ? "bg-gradient-to-r from-primary to-secondary hover:opacity-90 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] text-primary-foreground"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              }`}
            >
              Continua il processo di onboarding
              {checked && <ChevronRight className="w-5 h-5 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
